import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./logo.component";
import {
  Show,
  SignInButton,
  SignUpButton,
  UserButton,
  useUser,
  useAuth,
} from "@clerk/react";
import { useEffect } from "react";

import { syncUserWithBackend } from "../../services/clerk.service";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/stock-list", label: "The Stock List" },
  { to: "/chat", label: "Chat" },
  { to: "/watchlist", label: "My Watchlist" },
];

const UserSync = () => {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  useEffect(() => {
    if (isLoaded && user) {
      syncUserWithBackend(user, getToken);
    }
  }, [isLoaded, user, getToken]);

  return null;
};

const Header = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <style>{`
        .msm-header {
          position: sticky;
          top: 0;
          z-index: 100;
          background: var(--bg);
          border-bottom: 1px solid var(--border-subtle);
          font-family: var(--font-sans);
        }

        .msm-header-inner {
          max-width: 1100px;
          margin: 0 auto;
          padding: 0 var(--space-6);
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .msm-brand {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          text-decoration: none;
          color: inherit;
        }

        .msm-brand-text {
          display: flex;
          flex-direction: column;
          line-height: 1;
        }

        .msm-brand-name {
          font-family: var(--font-mono);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          letter-spacing: 0.02em;
          white-space: nowrap;
        }

        .msm-brand-sub {
          font-size: 12px;
          color: var(--text-tertiary);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          margin-top: 2px;
        }

        .msm-nav {
          display: flex;
          align-items: center;
          gap: var(--space-1);
        }

        .msm-nav-link {
          text-decoration: none;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-tertiary);
          padding: 6px 14px;
          border-radius: var(--radius-sm);
          transition: color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
          letter-spacing: 0.01em;
          white-space: nowrap;
        }

        .msm-nav-link:hover {
          color: var(--text-primary);
          background: var(--surface-2);
        }

        .msm-nav-link.active {
          color: var(--accent);
          background: var(--accent-soft);
        }

        /* Fund link gets a subtle accent to stand out */
        .msm-nav-link.fund {
          color: var(--accent);
          opacity: 0.7;
        }

        .msm-nav-link.fund:hover,
        .msm-nav-link.fund.active {
          opacity: 1;
          background: var(--accent-soft);
        }

        .msm-auth {
          display: flex;
          align-items: center;
          gap: var(--space-2);
        }

        .msm-btn-signin {
          font-family: var(--font-sans);
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          background: none;
          border: 1px solid var(--border-strong);
          padding: 6px 14px;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: color var(--dur-fast) var(--ease-out), border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
          white-space: nowrap;
        }

        .msm-btn-signin:hover {
          color: var(--text-primary);
          border-color: var(--text-tertiary);
          background: var(--surface-2);
        }

        .msm-btn-signup {
          font-family: var(--font-sans);
          font-size: 13px;
          font-weight: 600;
          color: var(--accent-ink);
          background: var(--accent);
          border: none;
          padding: 6px 14px;
          border-radius: var(--radius-sm);
          cursor: pointer;
          transition: background var(--dur-fast) var(--ease-out);
          white-space: nowrap;
        }

        .msm-btn-signup:hover { background: var(--accent-hover); }

        .msm-hamburger {
          display: none;
          flex-direction: column;
          justify-content: center;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: var(--radius-sm);
        }

        .msm-hamburger:hover { background: var(--surface-2); }

        .msm-hamburger span {
          display: block;
          width: 20px;
          height: 1.5px;
          background: var(--text-tertiary);
          border-radius: 2px;
          transition: transform 0.2s var(--ease-out), opacity 0.2s var(--ease-out);
        }

        .msm-hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
        .msm-hamburger.open span:nth-child(2) { opacity: 0; }
        .msm-hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

        .msm-mobile-menu {
          display: none;
          background: var(--bg);
          border-top: 1px solid var(--border-subtle);
          padding: 12px 24px 16px;
          flex-direction: column;
          gap: 2px;
        }

        .msm-mobile-menu.open { display: flex; }

        .msm-mobile-link {
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-tertiary);
          padding: 10px 12px;
          border-radius: var(--radius-md);
          transition: color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
        }

        .msm-mobile-link:hover { color: var(--text-primary); background: var(--surface-2); }
        .msm-mobile-link.active { color: var(--accent); background: var(--accent-soft); }

        .msm-mobile-auth {
          display: flex;
          gap: var(--space-2);
          padding: 10px 12px 4px;
          border-top: 1px solid var(--border-subtle);
          margin-top: var(--space-2);
        }

        @media (max-width: 600px) {
          .msm-nav { display: none; }
          .msm-auth { display: none; }
          .msm-hamburger { display: flex; }
        }
      `}</style>

      <Show when="signed-in">
        <UserSync />
      </Show>

      <header className="msm-header">
        <div className="msm-header-inner">
          <Link to="/" className="msm-brand">
            <Logo size={30} />
            <div className="msm-brand-text">
              <span className="msm-brand-name">Max's Stock Machine</span>
              <span className="msm-brand-sub">Value Investing</span>
            </div>
          </Link>

          <nav className="msm-nav">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={[
                  "msm-nav-link",
                  to === "/fund" ? "fund" : "",
                  location.pathname === to ? "active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {label}
              </Link>
            ))}
          </nav>

          <div className="msm-auth">
            <Show when="signed-out">
              <SignInButton mode="modal">
                <button className="msm-btn-signin">Sign In</button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="msm-btn-signup">Sign Up</button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <UserButton
                appearance={{
                  elements: { avatarBox: { width: 32, height: 32 } },
                }}
              />
            </Show>
          </div>

          <button
            className={`msm-hamburger${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div className={`msm-mobile-menu${menuOpen ? " open" : ""}`}>
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`msm-mobile-link${location.pathname === to ? " active" : ""}`}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </Link>
          ))}
          <div className="msm-mobile-auth">
            <Show when="signed-out">
              <SignInButton mode="modal">
                <button className="msm-btn-signin">Sign In</button>
              </SignInButton>
              <SignUpButton mode="modal">
                <button className="msm-btn-signup">Sign Up</button>
              </SignUpButton>
            </Show>
            <Show when="signed-in">
              <UserButton />
            </Show>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
